/**
 * Phase-level transition ACL per inventory_host_replacement_plan.md §5.
 *
 * - `manufacturing_plant` + matching `sub_role_tag` (e.g. `phase:flooring`) may
 *   move units into or out of that phase.
 * - No phase tags → view-only Kanban (cannot transition).
 * - `manufacturing_plant_supervisor` overrides all phases.
 * - `manufacturing_plant_manager` also bypasses phase tags (Slice D) but their
 *   Kanban list is scoped to in-production units (not Pre-build).
 * - Desk `manufacturing` umbrella + platform escape hatches also bypass
 *   (desk managers keep full move rights).
 */

export const MFG_PLANT_ROLE = "manufacturing_plant";
export const MFG_PLANT_SUPERVISOR_ROLE = "manufacturing_plant_supervisor";
export const MFG_PLANT_MANAGER_ROLE = "manufacturing_plant_manager";

const PHASE_TAG_PREFIX = "phase:";

const BYPASS_ROLES = new Set([
  MFG_PLANT_SUPERVISOR_ROLE,
  MFG_PLANT_MANAGER_ROLE,
  "manufacturing",
  "platform_owner",
  "platform_admin",
  "platform_dev",
  "admin",
  "owner",
  "org_admin",
  "org_owner",
]);

/** Roles that keep the full plant Kanban (including Pre-build / unconfirmed). */
const FULL_PIPELINE_LIST_ROLES = new Set([
  MFG_PLANT_SUPERVISOR_ROLE,
  "manufacturing",
  "platform_owner",
  "platform_admin",
  "platform_dev",
  "admin",
  "owner",
  "org_admin",
  "org_owner",
  MFG_PLANT_ROLE,
]);

export function canBypassBuildPipelinePhaseAcl(roles: readonly string[]): boolean {
  return roles.some((r) => BYPASS_ROLES.has(r));
}

/**
 * Plant managers (without desk/supervisor/floor) see only units already sent to
 * production — serial or build_started_at set. Desk + supervisors + floor
 * keep the full plant list including Pre-build.
 */
export function isPlantManagerProductionPipelineScope(roles: readonly string[]): boolean {
  if (!roles.includes(MFG_PLANT_MANAGER_ROLE)) return false;
  return !roles.some((r) => FULL_PIPELINE_LIST_ROLES.has(r));
}

export function isManufacturingPlantRole(roles: readonly string[]): boolean {
  return roles.includes(MFG_PLANT_ROLE);
}

/** Role tokens that are phase sub-role tags (`phase:…`). */
export function phaseSubRoleTagsFromRoles(roles: readonly string[]): string[] {
  return roles.filter((r) => r.startsWith(PHASE_TAG_PREFIX));
}

/**
 * Whether the user may transition a unit from `fromSubRoleTag` into `toSubRoleTag`.
 * `fromSubRoleTag` is null when the unit has no current phase (unassigned → first move).
 */
export function canTransitionBuildPipelinePhase(
  roles: readonly string[],
  fromSubRoleTag: string | null,
  toSubRoleTag: string
): boolean {
  if (canBypassBuildPipelinePhaseAcl(roles)) return true;

  // Spec: parent manufacturing_plant + matching tag(s). Without the parent,
  // phase tags alone do not authorize transitions.
  if (!isManufacturingPlantRole(roles)) return false;

  const tags = new Set(phaseSubRoleTagsFromRoles(roles));
  if (tags.size === 0) return false;

  const to = toSubRoleTag.trim();
  if (to && tags.has(to)) return true;

  const from = typeof fromSubRoleTag === "string" ? fromSubRoleTag.trim() : "";
  if (from && tags.has(from)) return true;

  return false;
}

/** UI helper: can the user move this unit into `toPhase.sub_role_tag` from `fromPhase`? */
export function canMoveUnitToPhase(
  roles: readonly string[],
  fromPhase: { sub_role_tag: string } | null,
  toPhase: { sub_role_tag: string }
): boolean {
  return canTransitionBuildPipelinePhase(
    roles,
    fromPhase?.sub_role_tag ?? null,
    toPhase.sub_role_tag
  );
}

/** Unit already left Pre-build / order-received (serial issued or build started). */
export function isUnitInProductionPipeline(unit: {
  serial: string | null;
  build_started_at: string | null;
}): boolean {
  return Boolean(unit.serial) || Boolean(unit.build_started_at);
}
